import { request } from './client.js';

export interface SignedAudioDto {
  readonly assetId: string;
  readonly url: string;
  readonly urlExpiresAt: string;
  readonly durationMs: number | null;
}
export interface VoiceDto {
  readonly id: string;
  readonly name: string;
  readonly status: 'draft' | 'ready' | 'failed';
  readonly sampleTranscript: string | null;
  readonly sample: SignedAudioDto | null;
}
export interface NarrationDto {
  readonly id: string | null;
  readonly bookId: string;
  readonly sceneId: string;
  readonly sceneIndex: number;
  readonly textRu: string;
  readonly source: 'cloned' | 'synthetic' | 'recorded' | null;
  readonly voiceId: string | null;
  readonly presetVoice: 'female' | 'male' | null;
  readonly generationId: string | null;
  readonly durationMs: number | null;
  readonly narrationStale: boolean;
  readonly status: 'draft' | 'queued' | 'generating' | 'ready' | 'failed';
  readonly lastError: string | null;
  readonly audio: SignedAudioDto | null;
}

export type NarrationVoiceSelection =
  | { readonly kind: 'preset'; readonly presetVoice: 'female' | 'male' }
  | { readonly kind: 'custom'; readonly voiceId: string };

export const narrationsApi = {
  voices: () => request<{ items: VoiceDto[] }>('/voices'),
  /**
   * `consentVersion` is not form input — it is the edition of the consent text this
   * build showed next to the box, and the record of what was agreed to (§2.6).
   */
  createVoice: (name: string, consentVersion: string) =>
    request<VoiceDto>('/voices', {
      method: 'POST',
      body: { name, consent: true, consentVersion },
    }),
  renameVoice: (voiceId: string, name: string) =>
    request<VoiceDto>(`/voices/${voiceId}`, { method: 'PATCH', body: { name } }),
  deleteVoice: (voiceId: string) =>
    request<{ id: string; deleted: true }>(`/voices/${voiceId}`, { method: 'DELETE' }),
  attachSample: (voiceId: string, assetId: string, sampleTranscript: string | null) =>
    request<VoiceDto>(`/voices/${voiceId}/sample`, {
      method: 'POST',
      body: { assetId, sampleTranscript },
    }),
  list: (bookId: string) => request<{ items: NarrationDto[] }>(`/books/${bookId}/narrations`),
  generateAll: (bookId: string, voice: NarrationVoiceSelection) =>
    request(`/books/${bookId}/narrations/generate`, { method: 'POST', body: voice }),
  generate: (sceneId: string, voice: NarrationVoiceSelection) =>
    request(`/scenes/${sceneId}/narration/generate`, { method: 'POST', body: voice }),
  attachRecorded: (sceneId: string, assetId: string) =>
    request<NarrationDto>(`/scenes/${sceneId}/narration/recorded`, {
      method: 'POST',
      body: { assetId, consent: true },
    }),
  remove: (sceneId: string) => request(`/scenes/${sceneId}/narration`, { method: 'DELETE' }),
};
